import React from "react";
import { Link } from "react-router-dom";
import "./Footer.scss";

import {
  HOME_PRODUCTS,
  MENU_EDU_CHAIN,
  MENU_DEVELOPER,
  MENU_FUNDING,
  SOCIAL_LINKS,
} from "../../../constants";

import logo from "../../../assets/images/home/10-footer/frame-63485.svg";
import twitter from "../../../assets/images/home/10-footer/twitter-icon.svg";
import linkedin from "../../../assets/images/home/10-footer/linkedin-icon.svg";
import telegram from "../../../assets/images/home/10-footer/telegram-icon.svg";
import youtube from "../../../assets/images/home/10-footer/youtube-icon.svg";

// Footer columns reuse the same destinations as the site header so they stay
// in sync. Each link is { label, href } and rendered by <FooterLink/>, which
// routes internal paths and opens external URLs in a new tab.
const toLink = (item) => ({ label: item.title, href: item.url });

const COLUMNS = [
  { title: "EDU Chain", links: MENU_EDU_CHAIN.map(toLink) },
  { title: "Products", links: HOME_PRODUCTS.map(toLink) },
  { title: "Developers", links: MENU_DEVELOPER.map(toLink) },
  { title: "Funding", links: MENU_FUNDING.map(toLink) },
];

const LEGAL = [
  { label: "User Docs", href: "https://userdocs.opencampus.xyz" },
  { label: "Terms of Use", href: "https://disclaimer.opencampus.xyz/main/" },
];

const SOCIALS = [
  { icon: twitter, alt: "X", href: SOCIAL_LINKS.twitter },
  { icon: linkedin, alt: "LinkedIn", href: SOCIAL_LINKS.linkedin },
  { icon: telegram, alt: "Telegram", href: "https://t.me/opencampus_xyz" },
  { icon: youtube, alt: "YouTube", href: SOCIAL_LINKS.youtube },
];

function isExternal(href) {
  return /^(https?:)?\/\//i.test(href) || /^(mailto:|tel:)/i.test(href);
}

function isHash(href) {
  return typeof href === "string" && href[0] === "#";
}

function FooterLink({ href, label, className }) {
  // In-page anchor (Minds / AI Toolkit / Open Campus ID): smooth-scroll to the
  // matching homepage section and stay on the homepage.
  if (isHash(href)) {
    return (
      <a
        className={className}
        href={href}
        onClick={(e) => {
          const el = document.querySelector(href);
          if (el) {
            e.preventDefault();
            el.scrollIntoView({ behavior: "smooth", block: "start" });
          }
        }}
      >
        {label}
      </a>
    );
  }
  if (isExternal(href)) {
    return (
      <a
        className={className}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
      >
        {label}
      </a>
    );
  }
  return (
    <Link className={className} to={href}>
      {label}
    </Link>
  );
}

export default function Footer() {
  return (
    <footer className="oc-footer">
      <div className="oc-footer__inner">
        <div className="oc-footer__top">
          <div className="oc-footer__brand">
            <img className="oc-footer__logo" src={logo} alt="Open Campus" />
            <p className="oc-footer__tagline">
              The AI-first platform for lifelong learning, credentials, and growth.
            </p>
            <div className="oc-footer__socials">
              {SOCIALS.map((s) => (
                <a
                  key={s.alt}
                  className="oc-footer__social"
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={s.alt}
                >
                  <img src={s.icon} alt={s.alt} />
                </a>
              ))}
            </div>
          </div>

          <nav className="oc-footer__cols">
            {COLUMNS.map((col) => (
              <div className="oc-footer__col" key={col.title}>
                <p className="oc-footer__col-title">{col.title}</p>
                <ul>
                  {col.links.map((l) => (
                    <li key={l.label}>
                      <FooterLink href={l.href} label={l.label} />
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </nav>
        </div>

        <div className="oc-footer__bottom">
          <p>© {new Date().getFullYear()} Open Campus. All rights reserved.</p>
          <div className="oc-footer__legal">
            {LEGAL.map((l) => (
              <FooterLink
                key={l.label}
                className="oc-footer__legal-link"
                href={l.href}
                label={l.label}
              />
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}
